import React, { useCallback, useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, type BackendUser } from '../types';
import { useApiCall } from '../hooks/useApiCall';
import { isMobileUserAgent } from '../utils/mobileUtils';
import { authLogger } from '../utils/logger';
import { getServiceHeaders } from '../utils/serviceUtils';
import { sessionGet } from '../utils/storage';
import { API_BASE } from '../utils/api';
import MaxIcon, { MAX_COLOR } from './MaxIcon';

interface MaxAuthProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const normalizeApiBase = (raw: string) => raw.trim().replace(/\/$/, '');

function backendUserToMaxUser(backend: BackendUser): User {
  const maxUnique = backend?.connection?.max;
  const mid = maxUnique != null && maxUnique !== '' ? Number.parseInt(String(maxUnique), 10) : Number.NaN;
  return {
    id: Number.isFinite(mid) ? mid : (backend.uid ?? 'unknown'),
    name: [backend.name, backend.surname].filter(Boolean).join(' ').trim() || 'MAX User',
    avatar: backend.photo ?? '',
    platform: 'max',
  };
}

/**
 * Вход через мессенджер MAX — по одноразовому коду из бота, как у Telegram:
 * бот присылает 5 цифр на «Начать» / `/start` / `/code`, фронт меняет код на токен
 * через `POST /oauth/max/login-code`.
 */
const MaxAuth: React.FC<MaxAuthProps> = ({ onSuccess, onCancel }: MaxAuthProps) => {
  const { state, login, setLoading, setError, sendToParent, redirectToReturnUrl } = useAuth();
  const { execute } = useApiCall();
  const [hasParentWindow, setHasParentWindow] = useState(true);
  const [hasReturnUrl, setHasReturnUrl] = useState(false);
  const [loginCode, setLoginCode] = useState('');

  const apiBase = normalizeApiBase(API_BASE);
  const codeBotUsername = (import.meta.env.VITE_MAX_LOGIN_CODE_BOT_USERNAME || '').trim().replace(/^@/, '');
  const maxBotDeepLink = codeBotUsername ? `https://max.ru/${codeBotUsername}?start=sso` : null;

  useEffect(() => {
    const hasOpener = !!window.opener;
    const hasParent = window.parent !== window;
    const parentAvailable = hasOpener || hasParent;
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');

    setHasParentWindow(parentAvailable);
    setHasReturnUrl(!!returnUrl);

    authLogger.info('Проверка родительского окна (MAX)', {
      hasOpener,
      hasParent,
      parentAvailable,
      hasReturnUrl: !!returnUrl,
      codeBot: !!codeBotUsername,
    });
  }, [codeBotUsername]);

  const fetchBackendUser = useCallback(
    async (token: string): Promise<BackendUser> => {
      const userResponse = await execute({
        url: `${apiBase}/user`,
        method: 'GET',
        headers: {
          'ssmm-token': token,
          ...getServiceHeaders(),
        },
      });
      if (!userResponse) {
        throw new Error('Не удалось загрузить профиль пользователя');
      }
      return userResponse as BackendUser;
    },
    [apiBase, execute]
  );

  const completeLogin = useCallback(
    async (token: string, user: User, backendUser: BackendUser) => {
      setLoginCode('');
      login(user, token, backendUser);

      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');

      if (returnUrl && isMobileUserAgent()) {
        redirectToReturnUrl(token);
      } else {
        sendToParent({
          type: 'AUTH_SUCCESS',
          data: {
            token,
            user: backendUser,
          },
        });
      }

      onSuccess?.();
    },
    [login, redirectToReturnUrl, sendToParent, onSuccess]
  );

  const submitLoginCode = useCallback(async () => {
    const normalized = loginCode.replace(/\D/g, '').slice(0, 5);
    if (normalized.length !== 5) {
      setError('Введите 5 цифр кода из MAX');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const qs = new URLSearchParams(window.location.search);
      const utmRaw = qs.get('utm') || qs.get('utm_string') || '';
      const loginCodeUrl =
        `${apiBase}/oauth/max/login-code` + (utmRaw ? `?${new URLSearchParams({ utm: utmRaw }).toString()}` : '');

      const result = await execute({
        url: loginCodeUrl,
        method: 'POST',
        data: { code: normalized },
        headers: {
          ...getServiceHeaders(),
        },
      });

      if (!result?.token) {
        throw new Error('Не получили токен от сервера');
      }

      const backendUser = await fetchBackendUser(result.token);
      const user = backendUserToMaxUser(backendUser);
      await completeLogin(result.token, user, backendUser);
    } catch (e: unknown) {
      const errorMessage =
        (e && typeof e === 'object' && 'message' in e && typeof (e as Error).message === 'string'
          ? (e as Error).message
          : null) || 'Ошибка входа по коду';
      setError(errorMessage);
      sendToParent({
        type: 'AUTH_ERROR',
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [apiBase, completeLogin, execute, fetchBackendUser, loginCode, sendToParent, setError, setLoading]);

  return (
    <div className="flex flex-col min-h-screen bg-zinc-900">
      {/* Header */}
      <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
        <button
          onClick={() => onCancel?.()}
          className="flex items-center mb-6 transition-colors text-zinc-500 hover:text-zinc-300"
          type="button"
        >
          <svg className="mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Назад</span>
        </button>

        <div className="flex items-center mb-2 space-x-3">
          <MaxIcon />
          <div>
            <h1 className="text-lg font-semibold text-white">MAX</h1>
            <p className="text-xs text-zinc-500">Вход через мессенджер</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6">
        {!hasParentWindow && !hasReturnUrl && (
          <div className="p-3 mb-4 rounded-xl border bg-amber-500/10 border-amber-500/30">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-amber-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs text-amber-400">
                Страница открыта напрямую. Для корректной работы откройте через приложение.
              </p>
            </div>
          </div>
        )}

        <div className="p-4 space-y-3 rounded-xl border bg-zinc-900 border-zinc-800">
          <p className="text-xs font-medium text-zinc-300">Вход по коду</p>
          <p className="text-xs leading-relaxed text-zinc-500">
            {maxBotDeepLink ? (
              <>
                Нажмите «Открыть бота», затем в MAX нажмите «Начать» или отправьте{' '}
                <span className="text-zinc-400">/code</span> — придёт одноразовый код. Введите его ниже.
              </>
            ) : (
              <>
                В MAX отправьте боту авторизации команду <span className="text-zinc-400">/start</span> или{' '}
                <span className="text-zinc-400">/code</span>, затем введите присланный код.
              </>
            )}
          </p>
          {maxBotDeepLink ? (
            <a
              href={maxBotDeepLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex justify-center items-center w-full py-2.5 text-sm font-medium text-white rounded-lg bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 transition-colors"
            >
              Открыть бота для получения кода
            </a>
          ) : null}
          <input
            type="text"
            autoComplete="one-time-code"
            inputMode="numeric"
            value={loginCode}
            onChange={(e) => {
              setLoginCode(e.target.value.replace(/\D/g, '').slice(0, 5));
              if (state.error) setError(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && void submitLoginCode()}
            placeholder="00000"
            aria-label="Код входа из MAX"
            className="w-full px-3 py-2.5 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#5B5CFF]/40"
          />
          <button
            type="button"
            onClick={() => void submitLoginCode()}
            style={{ backgroundColor: MAX_COLOR }}
            disabled={state.isLoading}
            className="w-full py-2.5 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {state.isLoading ? 'Проверяем код…' : 'Войти по коду'}
          </button>
          {state.error && (
            <div className="p-3 rounded-lg border bg-red-500/10 border-red-500/30">
              <p className="text-xs text-red-400">{state.error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaxAuth;
